import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Dashboard/Header";
import { tokens } from "../../../theme";
import axios from "axios";
import { schemeCategory10 } from "d3-scale-chromatic";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Shared TextField styles
const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.9rem",
    height: "45px",
    "& fieldset": { borderColor: "#771726" },
    "&:hover fieldset": { borderColor: "#771726" },
    "&.Mui-focused fieldset": { borderColor: "#771726" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    color: "#000000",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
};

// Current datetime-helper
const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtNoSeconds = (v) =>
  typeof v === "string" ? v.replace(/:\d{2}$/, "") : v;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1, bgcolor: "#222", border: "1px solid #555" }}>
      <Typography variant="caption" sx={{ color: "#ddd" }}>
        {fmtNoSeconds(label)}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name || entry.dataKey}: {entry.value}
        </Typography>
      ))}
    </Paper>
  );
}

const seriesOptions = [
    // Analógicas
    { key: "tempEvaporadorSalida", label: "Temp Evaporador Salida" },
    { key: "tempEvaporadorEntrada", label: "Temp Evaporador Entrada" },
    { key: "presionSuccion", label: "Presión Succión" },
    { key: "presionDescarga", label: "Presión Descarga" },
    { key: "sobrecalentamiento", label: "Sobrecalentamiento" },
    { key: "tempSuccion", label: "Temp Succión" },
    { key: "salidaVentiladorECCircuito1", label: "Salida Ventilador EC Circuito 1" },
    { key: "condicionActual", label: "Condición Actual" },

    // Digitales
    { key: "protectorVentiladorMotorComp1", label: "Protector Ventilador y Motor Comp 1" },
    { key: "protectorBajaPresionExvCircuito1", label: "Protector Baja Presión y EXV Circuito 1" },
    { key: "switchFlujoAguaHelada", label: "Switch Flujo Agua Helada" },
    { key: "encendidoCompresor1", label: "Encendido Compresor 1" },
    { key: "activacionValvulaExvC1", label: "Activación Válvula EXV C1" },
    { key: "ventilador1Cto1EC", label: "Ventilador 1 Cto. 1 EC" },
    { key: "ventilador2Cto1AC", label: "Ventilador 2 Cto. 1 AC" },
  ];

const GFSCV31Comp = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [idEquipo, setIdEquipo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(nowLocal());
  const [fechaFinal, setFechaFinal] = useState(nowLocal());

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  // Data state
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const MACHINE = "Full Service Cooling V3 1Comp";
  const QUERY = "Grafica";

  const toggleKey = (k) => {
    setSelectedKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const fetchRows = async () => {
    if (new Date(fechaFinal) < new Date(fechaInicio)) {
      setError("La fecha final no puede ser anterior a la fecha de inicio.");
      return;
    }

    setError(null);
    setLoading(true);
    setRows([]);

    try {
      const res = await axios.post(
        `${API_BASE}/test/consulta/${encodeURIComponent(MACHINE)}/${encodeURIComponent(QUERY)}`,
        { idEquipo, fechaInicio, fechaFinal },
        { headers: { "Content-Type": "application/json" } }
      );

      const body = res.data.body
        ? (typeof res.data.body === "string"
            ? JSON.parse(res.data.body)
            : res.data.body)
        : res.data;

      const extractedData = Array.isArray(body.data)
        ? body.data
        : (Array.isArray(body.data?.[0]) ? body.data[0] : []);

      const normalizeKeys = (row) => {
        const clean = {};
        for (const k in row) clean[k.trim()] = row[k];
        return clean;
      };

      const parseValue = (v) => {
        if (v === null || v === undefined || v === "") return null;
        const s = String(v).trim().toLowerCase();
        if (["true", "on", "encendido", "sí", "si", "1"].includes(s)) return 1;
        if (["false", "off", "apagado", "no", "0"].includes(s)) return 0;
        const n = Number(s.replace(",", "."));
        return Number.isFinite(n) ? n : v;
      };

      const mapped = extractedData.map((row, i) => {
        const r = normalizeKeys(row);
        const fechaUTC = new Date(r["Fecha"]);
        const ts = fechaUTC.getTime();

        const formattedDate =
          `${fechaUTC.getUTCFullYear()}-${String(fechaUTC.getUTCMonth() + 1).padStart(2, "0")}` +
          `-${String(fechaUTC.getUTCDate()).padStart(2, "0")} ` +
          `${String(fechaUTC.getUTCHours()).padStart(2, "0")}:` +
          `${String(fechaUTC.getUTCMinutes()).padStart(2, "0")}:` +
          `${String(fechaUTC.getUTCSeconds()).padStart(2, "0")}`;

        const base = {
          id: i + 1,
          ts,
          tipoMaquinaControl: r["Tipo de Máquina y Control"],
          numeroEquipo: r["Número de Equipo"],
          fecha: formattedDate,

          // Analógicas
          tempEvaporadorSalida: r["Temp Evaporador Salida"],
          tempEvaporadorEntrada: r["Temp Evaporador Entrada"],
          presionSuccion: r["Presión Succión"],
          presionDescarga: r["Presión Descarga"],
          sobrecalentamiento: r["Sobrecalentamiento"],
          tempSuccion: r["Temp Succión"],
          salidaVentiladorECCircuito1: r["Salida Ventilador EC Circuito 1"],
          condicionActual: r["Condición Actual"],

          // Digitales
          protectorVentiladorMotorComp1: r["Protector Ventilador y Motor Comp 1"],
          protectorBajaPresionExvCircuito1: r["Protector Baja Presión y EXV Circuito 1"],
          switchFlujoAguaHelada: r["Switch Flujo Agua Helada"],
          encendidoCompresor1: r["Encendido Compresor 1"],
          activacionValvulaExvC1: r["Activación Válvula EXV C1"],
          ventilador1Cto1EC: r["Ventilador 1 Cto. 1 EC"],
          ventilador2Cto1AC: r["Ventilador 2 Cto. 1 AC"],
        };

        const TEXT_KEYS = new Set(["id", "tipoMaquinaControl", "numeroEquipo", "fecha", "ts"]);
        Object.keys(base).forEach((k) => {
          if (!TEXT_KEYS.has(k)) base[k] = parseValue(base[k]);
        });

        return base;
      });

      mapped.sort((a, b) => {
        const ta = a.ts ?? Date.parse(a.fecha) ?? 0;
        const tb = b.ts ?? Date.parse(b.fecha) ?? 0;
        return ta - tb;
      });

      setRows(mapped);
    } catch (err) {
      setError("Error al consultar los datos. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const analogKeys = [
    "tempEvaporadorSalida",
    "tempEvaporadorEntrada",
    "presionSuccion",
    "presionDescarga",
    "sobrecalentamiento",
    "tempSuccion",
    "salidaVentiladorECCircuito1",
    "condicionActual",
  ];

  const digitalKeys = [
    "protectorVentiladorMotorComp1",
    "protectorBajaPresionExvCircuito1",
    "switchFlujoAguaHelada",
    "encendidoCompresor1",
    "activacionValvulaExvC1",
    "ventilador1Cto1EC",
    "ventilador2Cto1AC",
  ];

  const columns = [
    { field: "id", headerName: "ID", width: 90 },

    { field: "tipoMaquinaControl", headerName: "Tipo de Máquina y Control", minWidth: 220, flex: 1 },
    { field: "numeroEquipo", headerName: "Número de Equipo", minWidth: 160, flex: 1 },
    { field: "fecha", headerName: "Fecha", minWidth: 180, flex: 1 },

    // Analógicas
    { field: "tempEvaporadorSalida", headerName: "Temp Evaporador Salida", minWidth: 200, flex: 1 },
    { field: "tempEvaporadorEntrada", headerName: "Temp Evaporador Entrada", minWidth: 200, flex: 1 },
    { field: "presionSuccion", headerName: "Presión Succión", minWidth: 180, flex: 1 },
    { field: "presionDescarga", headerName: "Presión Descarga", minWidth: 180, flex: 1 },
    { field: "sobrecalentamiento", headerName: "Sobrecalentamiento", minWidth: 180, flex: 1 },
    { field: "tempSuccion", headerName: "Temp Succión", minWidth: 160, flex: 1 },
    { field: "salidaVentiladorECCircuito1", headerName: "Salida Ventilador EC Circuito 1", minWidth: 240, flex: 1 },
    { field: "condicionActual", headerName: "Condición Actual", minWidth: 180, flex: 1 },

    // Digitales
    { field: "protectorVentiladorMotorComp1", headerName: "Protector Ventilador y Motor Comp 1", minWidth: 280, flex: 1 },
    { field: "protectorBajaPresionExvCircuito1", headerName: "Protector Baja Presión y EXV Circuito 1", minWidth: 300, flex: 1 },
    { field: "switchFlujoAguaHelada", headerName: "Switch Flujo Agua Helada", minWidth: 220, flex: 1 },
    { field: "encendidoCompresor1", headerName: "Encendido Compresor 1", minWidth: 200, flex: 1 },
    { field: "activacionValvulaExvC1", headerName: "Activación Válvula EXV C1", minWidth: 220, flex: 1 },
    { field: "ventilador1Cto1EC", headerName: "Ventilador 1 Cto. 1 EC", minWidth: 200, flex: 1 },
    { field: "ventilador2Cto1AC", headerName: "Ventilador 2 Cto. 1 AC", minWidth: 200, flex: 1 },
  ];

  const dummyData = [
    { fecha: "1", value: 2 },
    { fecha: "2", value: 5.5 },
    { fecha: "3", value: 2 },
    { fecha: "5", value: 8.5 },
    { fecha: "8", value: 1.5 },
    { fecha: "10", value: 5 },
  ];

  const displayData = rows.length ? rows : dummyData;

  const seriesKeys = React.useMemo(() => {
    const displaySeriesKeys = seriesOptions
      .filter((opt) => selectedKeys.includes(opt.key))
      .map((opt) => opt.key);

    return displaySeriesKeys.length
      ? displaySeriesKeys
      : (rows.length ? [] : ["value"]);
  }, [selectedKeys, rows.length]);

  const lineColors = React.useMemo(
    () => seriesKeys.map((_, i) => schemeCategory10[i % schemeCategory10.length]),
    [seriesKeys]
  );

  const labelMap = React.useMemo(() => {
    const m = Object.fromEntries(seriesOptions.map((o) => [o.key, o.label]));
    m.value = "Valor (demo)";
    return m;
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", mt: "90px", px: 2 }}>
      <Header
        title="Full Service Cooling V3 1Comp"
        sxTitle={{ fontSize: "2rem", textAlign: "center" }}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="ID Equipo"
              variant="outlined"
              fullWidth
              value={idEquipo}
              onChange={(e) => setIdEquipo(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Inicio"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Final"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaFinal}
              onChange={(e) => setFechaFinal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2, height: 400, display: "flex", flexDirection: "column" }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              <Tab label="Análogos" />
              <Tab label="Digitales" />
            </Tabs>

            {tabIndex === 0 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, "& .MuiFormControlLabel-root": { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter((opt) => analogKeys.includes(opt.key))
                    .map((opt) => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={
                            <Typography variant="caption" sx={{ fontSize: 12, fontWeight: "bold" }}>
                              {opt.label}
                            </Typography>
                          }
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            {tabIndex === 1 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, "& .MuiFormControlLabel-root": { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter((opt) => digitalKeys.includes(opt.key))
                    .map((opt) => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption" fontWeight="bold">{opt.label}</Typography>}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            <Button
              variant="contained"
              sx={{
                bgcolor: "#771726",
                color: "white",
                mt: 1,
                width: isMobile ? "100%" : "auto",
                "&:hover": { bgcolor: "#747274" },
              }}
              onClick={(e) => {
                e.preventDefault();
                fetchRows();
              }}
            >
              Graficar
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{
              height: 400,
              p: 2,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableColumnMenu
                pageSize={10}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#771726",
                    zIndex: 1,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#771726",
                    color: "#ffffff",
                    borderTop: "none",
                  },
                  "& .MuiTablePagination-root, & .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container mb={3}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ height: 400, p: 2, pb: 4 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                    mb: 1,
                    ml: "30px",
                    width: "calc(100% - 60px)",
                  }}
                >
                  {seriesKeys.map((key, i) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: lineColors[i],
                          mr: 1,
                        }}
                      />
                      <Typography variant="caption">{labelMap[key]}</Typography>
                    </Box>
                  ))}
                </Box>

                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    animationDuration={0}
                    data={displayData}
                    margin={{ top: 20, right: 30, left: -30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke={colors.grey[700]} />

                    <XAxis
                      dataKey="fecha"
                      interval="auto"
                      minTickGap={70}
                      tickMargin={10}
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      tickFormatter={fmtNoSeconds}
                    />

                    <YAxis
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      domain={["auto", "auto"]}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      delayShow={0}
                      cursor={{ stroke: colors.grey[500], strokeDasharray: "3 3" }}
                    />

                    {seriesKeys.map((key, i) => (
                      <Line
                        key={key}
                        dataKey={key}
                        name={labelMap[key]}
                        stroke={lineColors[i]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GFSCV31Comp;